/**
 * Client-side encoding for `EncryptedMandateBook` and `ConfidentialRequestBook`.
 *
 * WHY ORDER IS A CONTRACT. Both books take a flat `bytes[] proofs` alongside a struct of external
 * handles, and validate them positionally — Solidity gives no way to name a proof. If the client
 * and the contract disagree about the order, the submission either reverts on a type mismatch or,
 * worse, seals a mandate whose market caps are its rate indexes. The order is therefore defined
 * once, here, and the contracts publish the same order through `mandateHandleOrder()` and
 * `requestHandleOrder()` so the two can be compared rather than assumed.
 *
 * WHY THE SLOTS ARE FIXED-LENGTH. Every mandate is 35 handles and every request is 19, whatever the
 * provider actually enabled. Unused slots carry encrypted zero. A variable-length submission would
 * leak the number of markets a provider is willing to lend into — which is exactly the sort of
 * shape inference PRD §8.3 exists to prevent.
 */
import { assertFitsType, NoxTypeError } from "./types.js";
/** Slot counts, mirroring the constants the two contracts expose. */
export const MARKET_SLOTS = 8;
export const COLLATERAL_FAMILY_SLOTS = 4;
export const MATURITY_BUCKET_SLOTS = 4;
export const MANDATE_HANDLE_COUNT = 1 + 8 + 8 + 8 + 4 + 4 + 1 + 1;
export const REQUEST_HANDLE_COUNT = 1 + 1 + 8 + 8 + 1;
/** The layout version both books stamp into their commitments. Bump together, never apart. */
export const KYRVE_SCHEMA_VERSION = 1;
/** The canonical mandate order. Must equal `EncryptedMandateBook.mandateHandleOrder()`. */
export function mandateFields(mandate) {
    const fields = [{ name: "totalBudget", value: mandate.totalBudget, type: "euint256" }];
    pushSlots(fields, "marketCaps", mandate.marketCaps, MARKET_SLOTS, "euint256");
    pushSlots(fields, "minRateIndexes", mandate.minRateIndexes, MARKET_SLOTS, "euint16");
    pushSlots(fields, "enabledFlags", mandate.enabledFlags, MARKET_SLOTS, "euint16");
    pushSlots(fields, "collateralFamilyCaps", mandate.collateralFamilyCaps, COLLATERAL_FAMILY_SLOTS, "euint256");
    pushSlots(fields, "maturityBucketCaps", mandate.maturityBucketCaps, MATURITY_BUCKET_SLOTS, "euint256");
    fields.push({
        name: "maxDurationIndex",
        value: BigInt(mandate.maxDurationIndex),
        type: "euint16",
    });
    fields.push({
        name: "allocationWeight",
        value: BigInt(mandate.allocationWeight),
        type: "euint16",
    });
    assertCount(fields.length, MANDATE_HANDLE_COUNT, "mandate");
    return fields;
}
/** The canonical request order. Must equal `ConfidentialRequestBook.requestHandleOrder()`. */
export function requestFields(request) {
    const fields = [
        { name: "desiredAssets", value: request.desiredAssets, type: "euint256" },
        { name: "minimumAssets", value: request.minimumAssets, type: "euint256" },
    ];
    pushSlots(fields, "maxRateIndexes", request.maxRateIndexes, MARKET_SLOTS, "euint16");
    pushSlots(fields, "enabledFlags", request.enabledFlags, MARKET_SLOTS, "euint16");
    fields.push({
        name: "preferredMaturityIndex",
        value: BigInt(request.preferredMaturityIndex),
        type: "euint16",
    });
    assertCount(fields.length, REQUEST_HANDLE_COUNT, "request");
    return fields;
}
/**
 * Encrypts a whole mandate for one book contract.
 *
 * Every handle is bound to `book`, to this chain, to the caller's wallet and to a 3600 second
 * expiry. Submitting them to a different contract, from a different wallet, on a different chain,
 * or an hour later, all fail inside NoxCompute — proven in the Phase 2 suite rather than assumed.
 */
export async function encryptMandate(client, book, mandate) {
    const fields = mandateFields(mandate);
    const inputs = await client.encryptAll(fields, book);
    const handles = inputs.map((input) => input.handle);
    let cursor = 0;
    const take = () => handles[cursor++];
    const takeMany = (count) => Array.from({ length: count }, () => take());
    return {
        inputs,
        proofs: inputs.map((input) => input.proof),
        struct: {
            totalBudget: take(),
            marketCaps: takeMany(MARKET_SLOTS),
            minRateIndexes: takeMany(MARKET_SLOTS),
            enabledFlags: takeMany(MARKET_SLOTS),
            collateralFamilyCaps: takeMany(COLLATERAL_FAMILY_SLOTS),
            maturityBucketCaps: takeMany(MATURITY_BUCKET_SLOTS),
            maxDurationIndex: take(),
            allocationWeight: take(),
        },
    };
}
/** Encrypts a whole request for one book contract. Same binding rules as {encryptMandate}. */
export async function encryptRequest(client, book, request) {
    const fields = requestFields(request);
    const inputs = await client.encryptAll(fields, book);
    const handles = inputs.map((input) => input.handle);
    let cursor = 0;
    const take = () => handles[cursor++];
    const takeMany = (count) => Array.from({ length: count }, () => take());
    return {
        inputs,
        proofs: inputs.map((input) => input.proof),
        struct: {
            desiredAssets: take(),
            minimumAssets: take(),
            maxRateIndexes: takeMany(MARKET_SLOTS),
            enabledFlags: takeMany(MARKET_SLOTS),
            preferredMaturityIndex: take(),
        },
    };
}
export function mandateDisclosure(provider, universeId, epoch, mandate) {
    return {
        publicFields: [
            { name: "provider", value: provider },
            { name: "universe", value: universeId },
            { name: "epoch", value: String(epoch) },
            { name: "schema version", value: String(KYRVE_SCHEMA_VERSION) },
            { name: "submission time", value: "the block timestamp of this transaction" },
            { name: "commitment", value: "a keccak over the handle references, not over any value" },
        ],
        privateFields: mandateFields(mandate).map((field) => field.name),
        // Nothing in a mandate crosses the boundary. It is sealed encrypted and stays encrypted.
        permanentDisclosureWarning: null,
    };
}
export function requestDisclosure(borrower, universeId, bondWei, expiresAt, request) {
    return {
        publicFields: [
            { name: "borrower", value: borrower },
            { name: "universe", value: universeId },
            { name: "bond", value: `${bondWei} wei — visible, it is ETH` },
            { name: "expiry", value: String(expiresAt) },
            { name: "exact-fill requirement", value: "public, the keeper must agree on it" },
            { name: "collateral reference", value: "public, collateral is a public Midnight position" },
            { name: "nonce", value: "public, it is the replay guard" },
        ],
        privateFields: requestFields(request).map((field) => field.name),
        permanentDisclosureWarning: null,
    };
}
function pushSlots(fields, name, values, slots, type) {
    if (values.length > slots) {
        throw new NoxTypeError(`${name} has ${values.length} entries but the universe has ${slots} slots. A longer array ` +
            "cannot be padded away and would change the submission shape, which is itself a leak.");
    }
    for (let i = 0; i < slots; i++) {
        // Absent slots are encrypted ZERO, not omitted. Every mandate must have the same shape.
        const raw = values[i] ?? 0;
        const value = typeof raw === "bigint" ? raw : BigInt(raw);
        assertFitsType(value, type);
        fields.push({ name: `${name}[${i}]`, value, type });
    }
}
function assertCount(actual, expected, what) {
    if (actual !== expected) {
        throw new NoxTypeError(`${what} encoding produced ${actual} fields, but the contract expects exactly ${expected}. ` +
            "The two must never drift: the book validates proofs positionally.");
    }
}
//# sourceMappingURL=books.js.map